import { useState, useEffect } from 'react';
import { Loader2, Edit2, AlertTriangle, X } from 'lucide-react';

interface RenameClientModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentClientName: string;
    onConfirmRename: (newName: string) => Promise<void>;
}

export function RenameClientModal({
    isOpen,
    onClose,
    currentClientName,
    onConfirmRename,
}: RenameClientModalProps) {
    const [newName, setNewName] = useState(currentClientName);
    const [isRenaming, setIsRenaming] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            setNewName(currentClientName);
            setError(null);
        }
    }, [isOpen, currentClientName]);

    const handleConfirm = async () => {
        if (!newName.trim()) {
            setError('Client name cannot be empty');
            return;
        }
        if (newName === currentClientName) {
            onClose();
            return;
        }

        setIsRenaming(true);
        setError(null);
        try {
            await onConfirmRename(newName);
            onClose();
        } catch (err: any) {
            setError(err.message || 'Error renaming client');
        } finally {
            setIsRenaming(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden animate-in zoom-in-95 duration-200">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
                    <div className="flex items-center gap-2">
                        <div className="p-2 bg-amber-50 rounded-full">
                            <Edit2 className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                            <h3 className="font-bold text-[#454240]">Rename Client</h3>
                            <p className="text-xs text-gray-500">Update client name across the system</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">

                    {error && (
                        <div className="flex items-start gap-3 p-3 text-sm text-red-600 bg-red-50 rounded-md">
                            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-semibold text-[#454240] mb-2">
                                New Client Name
                            </label>
                            <input
                                type="text"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                className="w-full h-10 px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all shadow-sm"
                                placeholder="Enter new client name"
                                disabled={isRenaming}
                                autoFocus
                            />
                        </div>

                        <div className="flex items-start gap-2 text-xs text-gray-500 italic bg-gray-50 p-3 rounded border border-gray-100">
                            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                            <p>
                                This will update the client name in the master list and for <strong>ALL</strong> associated commercial actions. This change will also trigger a sync to SAP.
                            </p>
                        </div>
                    </div>

                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
                    <button
                        onClick={onClose}
                        disabled={isRenaming}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200 transition-colors shadow-sm"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={isRenaming || !newName.trim()}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-600 transition-all shadow-sm"
                    >
                        {isRenaming ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>Renaming...</span>
                            </>
                        ) : (
                            <>
                                <span>Confirm Rename</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
